import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  setMeta("property", "og:url", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export default function SeoManager() {
  const location = useLocation();

  // Canonical URL — refreshed on every route change.
  useEffect(() => {
    setCanonical(window.location.origin + location.pathname);
  }, [location.pathname]);

  // Meta tags + Organization structured data — loaded once from settings.
  useEffect(() => {
    api.get("/settings").then((r) => {
      const d = r.data || {};
      const origin = window.location.origin;
      if (d.seo_title) document.title = d.seo_title;
      setMeta("name", "description", d.seo_description);
      setMeta("name", "keywords", d.seo_keywords);
      setMeta("property", "og:site_name", "Around The World");
      setMeta("property", "og:title", d.seo_title || document.title);
      setMeta("property", "og:description", d.seo_description);
      setMeta("property", "og:type", "website");
      setMeta("name", "twitter:card", "summary_large_image");
      setMeta("name", "twitter:title", d.seo_title || document.title);
      setMeta("name", "twitter:description", d.seo_description);
      const logo = origin + "/brand/logo-seal.png";
      let ogImage = logo;
      if (d.seo_og_image) {
        ogImage = d.seo_og_image.startsWith("http") ? d.seo_og_image : origin + d.seo_og_image;
        setMeta("property", "og:image", ogImage);
        setMeta("name", "twitter:image", ogImage);
      }

      const sameAs = [];
      if (d.instagram_url) sameAs.push(d.instagram_url);

      setJsonLd("ld-organization", {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Around The World",
        alternateName: "ATW",
        url: origin,
        logo,
        image: ogImage,
        description: d.seo_description || "Luxury perfumes, watches, tailoring and candles by Around The World.",
        ...(d.footer_contact ? { email: (String(d.footer_contact).match(/[\w.+-]+@[\w-]+\.[\w.-]+/) || [])[0] } : {}),
        ...(sameAs.length ? { sameAs } : {}),
      });

      setJsonLd("ld-website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Around The World",
        url: origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${origin}/shop?cat={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      });
    }).catch(() => {});
  }, []);

  return null;
}
